import { ReactNode, useContext, useEffect, useRef, useState } from 'react'
import { FieldValues, useController, useWatch } from 'react-hook-form'
import { TZDate, tzOffset } from '@date-fns/tz'
import { format } from 'date-fns'
import { SelectionProps } from '../../../ui/form/Field'
import { useTranslation } from 'react-i18next'
import { ProjectContext } from '../../../contexts'
import { Column, Columns } from '../../../ui'
import TextInput from '../../../ui/form/TextInput'
import { parseUtcDate } from '../../../utils'

interface DateTimeFieldProps<X extends FieldValues> extends SelectionProps<X> {
    label: string
    required?: boolean
    dateLabel?: string
    timeLabel?: string
    notice?: ReactNode
}

const splitInTimezone = (value: unknown, timezone: string) => {
    const date = parseUtcDate(value)
    if (!date) return { date: '', time: '' }
    const zoned = new TZDate(date, timezone)
    return { date: format(zoned, 'yyyy-MM-dd'), time: format(zoned, 'HH:mm') }
}

const wallClockToUtc = (date: string, time: string, timezone: string) => {
    const [year, month, day] = date.split('-').map(Number)
    const [hours, minutes] = time.split(':').map(Number)
    const guess = Date.UTC(year, month - 1, day, hours, minutes)
    const first = guess - tzOffset(timezone, new Date(guess)) * 60000
    return new Date(guess - tzOffset(timezone, new Date(first)) * 60000)
}

export default function DateTimeField<X extends FieldValues>({
    name,
    control,
    required,
    dateLabel,
    timeLabel,
    notice,
}: DateTimeFieldProps<X>) {
    const [project] = useContext(ProjectContext)
    const { t } = useTranslation()

    const incomplete = useRef(false)
    const { field: { onChange }, fieldState: { error } } = useController({
        control,
        name,
        rules: {
            required,
            validate: () => !incomplete.current || t('date_time_incomplete'),
        },
    })
    const value = useWatch({ control, name })

    const [parts, setParts] = useState(() => splitInTimezone(value, project.timezone))
    const emitted = useRef<unknown>(value)

    useEffect(() => {
        if (value === emitted.current) return
        emitted.current = value
        incomplete.current = false
        setParts(splitInTimezone(value, project.timezone))
    }, [value, project.timezone])

    const emit = (next: { date: string, time: string }) => {
        setParts(next)
        incomplete.current = !next.date !== !next.time
        if (next.date && next.time) {
            emitted.current = wallClockToUtc(next.date, next.time, project.timezone).toISOString()
        } else if (!next.date && !next.time) {
            emitted.current = undefined
        } else {
            return
        }
        onChange(emitted.current)
    }

    return <div className="date-time">
        <Columns>
            <Column>
                <TextInput<string>
                    type="date"
                    name="date"
                    label={dateLabel ?? t('send_at_date')}
                    onChange={date => emit({ ...parts, date })}
                    value={parts.date}
                    required={required} />
            </Column>
            <Column>
                <TextInput<string>
                    type="time"
                    name="time"
                    label={timeLabel ?? t('send_at_time')}
                    onChange={time => emit({ ...parts, time })}
                    value={parts.time}
                    required={required} />
            </Column>
        </Columns>
        {error?.message && <span className="field-error">{error.message}</span>}
        <span className="label-subtitle">
            {notice ?? t('send_at_timezone_notice')}
        </span>
    </div>
}
